import React, { useState } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  onValidate?: (value: string) => string | undefined;
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      onValidate,
      validateOnBlur = true,
      validateOnChange = false,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const [internalError, setInternalError] = useState<string | undefined>();

    // Gerar IDs únicos para acessibilidade
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${inputId}-error`;
    const helperId = `${inputId}-helper`;

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (validateOnBlur && onValidate) {
        const validationError = onValidate(e.target.value);
        setInternalError(validationError);
      }
      props.onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (validateOnChange && onValidate) {
        const validationError = onValidate(e.target.value);
        setInternalError(validationError);
      }
      props.onChange?.(e);
    };

    const displayError = error || internalError;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-semibold text-neutral-700 mb-1.5"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={!!displayError}
          aria-describedby={
            displayError ? errorId : helperText ? helperId : undefined
          }
          className={`
            w-full px-4 py-2.5
            bg-white border rounded-lg
            shadow-sm
            transition-all duration-150
            focus:outline-none focus:ring-2
            placeholder:text-neutral-400
            ${
              displayError
                ? 'border-danger-500 focus:ring-danger-200 focus:border-danger-500 bg-danger-50'
                : 'border-neutral-200 focus:ring-primary-200 focus:border-primary-500'
            }
            ${className}
          `}
          onBlur={handleBlur}
          onChange={handleChange}
          {...props}
        />
        {displayError && (
          <p
            id={errorId}
            className="mt-1.5 text-sm text-danger-600 flex items-start gap-1"
            role="alert"
          >
            <span className="mt-0.5">⚠</span>
            <span>{displayError}</span>
          </p>
        )}
        {helperText && !displayError && (
          <p id={helperId} className="mt-1.5 text-xs text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
